import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { SiteFooter } from "@/components/Disclaimers";
import CookieConsent from "@/components/CookieConsent";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Assistant from "@/pages/Assistant";
import ServicesExplorer from "@/pages/ServicesExplorer";
import Providers from "@/pages/Providers";
import Booking from "@/pages/Booking";
import Journey from "@/pages/Journey";
import Dashboard from "@/pages/Dashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Cities from "@/pages/Cities";
import CityDetail from "@/pages/CityDetail";
import Compare from "@/pages/Compare";
import MapView from "@/pages/MapView";
import About from "@/pages/About";
import { CompareProvider } from "@/context/CompareContext";
import CompareBar from "@/components/CompareBar";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminRevenue from "@/admin/AdminRevenue";
import AdminProviders from "@/admin/AdminProviders";
import AdminBookings from "@/admin/AdminBookings";
import AdminUsers from "@/admin/AdminUsers";
import AdminServices from "@/admin/AdminServices";
import AdminCompliance from "@/admin/AdminCompliance";
import AdminSettings from "@/admin/AdminSettings";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!(user.is_admin || user.role === "admin")) return <Navigate to="/dashboard" replace />;
  return children;
}

function SiteChrome({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <Navbar />}
      {children}
      {!isAdmin && <CompareBar />}
      {!isAdmin && <SiteFooter />}
      {!isAdmin && <CookieConsent />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CompareProvider>
          <SiteChrome>
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/services" element={<ServicesExplorer />} />
            <Route path="/cities" element={<Cities />} />
            <Route path="/cities/:slug" element={<CityDetail />} />
            <Route path="/providers" element={<Providers />} />
            <Route path="/map" element={<MapView />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/assistant" element={<Protected roles={["patient"]}><Assistant /></Protected>} />
            <Route path="/book" element={<Protected roles={["patient"]}><Booking /></Protected>} />
            <Route path="/booking" element={<Protected roles={["patient"]}><Booking /></Protected>} />
            <Route path="/journey/:id" element={<Protected><Journey /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
            <Route path="/admin" element={<AdminProtected><AdminLayout /></AdminProtected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="revenue" element={<AdminRevenue />} />
              <Route path="providers" element={<AdminProviders />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="compliance" element={<AdminCompliance />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SiteChrome>
        </CompareProvider>
      </BrowserRouter>
    </div>
  );
}
